/**
 * apps/web :: entitlement :: EntitlementGateContext
 *
 * Single source of truth for "does this authenticated + onboarded user
 * still have any product access?" — used by:
 *
 *   - `<RequiresEntitlement>` route guard to redirect expired users to
 *     /dashboard.
 *   - `<TopNav>` to collapse the nav to {Dashboard, Profile} when
 *     `isExpired === true`.
 *   - `<DashboardPage>` to swap the body for `<TrialExpiredPaywall>`.
 *   - `<ProfilePage>` Membership section (always rendered — even on
 *     expired) to read the cached entitlement list.
 *
 * Lifecycle:
 *   - Mounts INSIDE `<ProtectedRoute>` + `<OnboardingGate>` in App.tsx,
 *     so we assume an authenticated, onboarded user. Anonymous and
 *     pre-onboarded users never see this provider's fetch.
 *   - Fires GET /api/entitlements/me once on mount.
 *   - Exposes `refresh()` so /checkout/success and the Profile page's
 *     cancel/reactivate flows can invalidate the cache.
 *
 * Failure-mode contract: if the fetch errors (network / 4xx / 5xx),
 * the gate fails CLOSED — `isExpired = true`, `isLoading = false`. A
 * paywall in error is recoverable; silently granting access on a 5xx
 * is a revenue and product-correctness regression.
 *
 * "Expired" semantics: `isExpired === true` iff zero entitlements have
 * `expires_at_ms > now_ms`. Strict greater-than mirrors the backend
 * trial/entitlement check (`/api/entitlements/me/check` on the server
 * uses `> now`, not `>=`). At the boundary (`expires_at_ms === now_ms`)
 * the row is considered expired.
 *
 * Server-clock authority: `nowMs` returned in the context is the server
 * clock from the response body — NOT `Date.now()`. The whole reason the
 * endpoint sends `now_ms` is to defend against client-clock skew.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  listMyEntitlements,
  type Entitlement,
} from "@/lib/entitlement-api";

/** Reactive snapshot exposed by `useEntitlement()`. */
export interface EntitlementGateValue {
  /** True while the initial /api/entitlements/me probe is in flight. */
  isLoading: boolean;
  /** True iff zero entitlements satisfy `expires_at_ms > nowMs`. */
  isExpired: boolean;
  /** Entitlements that have not yet expired at `nowMs`. */
  activeEntitlements: Entitlement[];
  /** Server clock from the most-recent /api/entitlements/me response. */
  nowMs: number;
  /** Re-fetch /api/entitlements/me. Used after checkout / cancel / reactivate. */
  refresh: () => Promise<void>;
}

const EntitlementGateContext = createContext<EntitlementGateValue | null>(null);

interface ProviderState {
  loading: boolean;
  entitlements: Entitlement[];
  nowMs: number;
}

const INITIAL_STATE: ProviderState = {
  loading: true,
  entitlements: [],
  // Pre-fetch the now_ms field is unknowable; 0 is a safe sentinel because
  // every real entitlement has expires_at_ms > 0, and `isExpired` is
  // computed as a derived value below — consumers should not branch on
  // `nowMs` while `isLoading` is true.
  nowMs: 0,
};

export interface EntitlementGateProviderProps {
  children: ReactNode;
  /**
   * Test-only escape hatch — when true, the provider mounts in the
   * "post-fetch, no entitlements" state without firing fetch. Lets unit
   * tests drive consumers through the expired path without staging a
   * fetch mock. Production must always leave this false.
   */
  skipInitialFetch?: boolean;
  /**
   * Test-only seed for the initial state when `skipInitialFetch` is true.
   * Callers can hand-craft a "trial active" or "post-checkout" snapshot.
   */
  initialState?: { entitlements: Entitlement[]; nowMs: number };
}

export function EntitlementGateProvider({
  children,
  skipInitialFetch = false,
  initialState,
}: EntitlementGateProviderProps): JSX.Element {
  const [state, setState] = useState<ProviderState>(() => {
    if (skipInitialFetch) {
      return {
        loading: false,
        entitlements: initialState?.entitlements ?? [],
        nowMs: initialState?.nowMs ?? Date.now(),
      };
    }
    return INITIAL_STATE;
  });

  // mountedRef lets us guard setState calls so an unmounted provider
  // doesn't trigger React's "memory-leak" warning when fetch resolves
  // after teardown.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const resp = await listMyEntitlements();
      if (!mountedRef.current) return;
      setState({
        loading: false,
        entitlements: resp.entitlements,
        nowMs: resp.now_ms,
      });
    } catch {
      // Fail closed — paywall in error rather than silently grant access.
      // Surface no-active-entitlements + a server-clock fallback to Date.now()
      // so downstream `expires_at_ms > nowMs` checks still behave (no real
      // entitlement will satisfy the comparison once we've zeroed the list).
      if (!mountedRef.current) return;
      setState({
        loading: false,
        entitlements: [],
        nowMs: Date.now(),
      });
    }
  }, []);

  useEffect(() => {
    if (skipInitialFetch) return;
    void refresh();
  }, [refresh, skipInitialFetch]);

  const value = useMemo<EntitlementGateValue>(() => {
    const active = state.entitlements.filter(
      (ent) => ent.expires_at_ms > state.nowMs,
    );
    return {
      isLoading: state.loading,
      isExpired: active.length === 0,
      activeEntitlements: active,
      nowMs: state.nowMs,
      refresh,
    };
  }, [state, refresh]);

  return (
    <EntitlementGateContext.Provider value={value}>
      {children}
    </EntitlementGateContext.Provider>
  );
}

/**
 * Default value returned by {@link useEntitlement} when no provider is
 * mounted. Fails OPEN — `isExpired = false` — so legacy surfaces that
 * never got wrapped in the provider keep their pre-PR behaviour. This
 * is the opposite of the in-provider failure mode (`isExpired = true`
 * on fetch error) by design: in-provider, you've explicitly opted into
 * the gate and a 5xx must paywall; outside the provider, you've not
 * opted in at all and the gate is a no-op.
 */
const DEFAULT_GATE_VALUE: EntitlementGateValue = {
  isLoading: false,
  isExpired: false,
  activeEntitlements: [],
  nowMs: 0,
  refresh: async () => undefined,
};

/**
 * Read the entitlement gate state from context.
 *
 * Returns {@link DEFAULT_GATE_VALUE} (no expiry, no loading) when no
 * provider is mounted above the consumer. This is intentional — many
 * existing tests and a few non-paid surfaces (notifications, account
 * deletion) sit outside the post-onboarding route group where the
 * provider lives. Throwing would force every test harness in the tree
 * to wrap their renders, which is a lot of churn for no gain. Failing
 * "open" preserves pre-PROFILE-PAYWALL behaviour for any consumer
 * that isn't inside the gate, which is the principled default.
 *
 * Consumers that NEED a real gate value
 * (RequiresEntitlement, ProfilePage, DashboardPage, LearnerPortalShell
 * sidebar gating) all mount inside the provider; the test harnesses
 * for those components stage the provider explicitly.
 *
 * To assert "you forgot the provider" in a test, use
 * {@link useStrictEntitlement} which re-throws.
 */
export function useEntitlement(): EntitlementGateValue {
  const ctx = useContext(EntitlementGateContext);
  return ctx ?? DEFAULT_GATE_VALUE;
}

/**
 * Strict variant of {@link useEntitlement} — throws when used outside a
 * provider. Reserved for the unit-test harness so the "missing
 * provider" failure mode is still pinned on the context module itself.
 */
export function useStrictEntitlement(): EntitlementGateValue {
  const ctx = useContext(EntitlementGateContext);
  if (ctx === null) {
    throw new Error(
      "useStrictEntitlement() must be used inside <EntitlementGateProvider>",
    );
  }
  return ctx;
}
